// Libs
import {
  BrowserRouter,
  Routes,
  Navigate,
  Route,
  useLocation,
} from 'react-router-dom';
import { lazy } from 'react';

// Components
import MainLayout from '@app/components/layouts/MainLayout/MainLayout';
import Auth from '@app/pages/Auth/Auth';
import Login from '@app/pages/Auth/Login/Login';
import NotFound from '@app/components/common/NotFound/NotFound';

// Hocs
import { withLoading } from '@app/hocs/withLoading.hoc';

// Hooks
import useOwnerSession from '@app/hooks/useOwnerSession';

// Constants
import { PAGE_URL } from '@app/constants/route';

// Utils
import {
  clearOwnerSession,
  getOwnerToken,
  isSessionExpired,
} from '@app/utils/ownerAuth';

// Cổng auth luồng owner: KHÔNG ping :3865 nữa — chỉ kiểm tra token owner (localStorage).
// Ba lớp bảo vệ: (1) ở đây — chặn vào route riêng tư khi thiếu token HOẶC token đã quá
// hạn `expiresAt`; (2) useOwnerSession — hẹn giờ tự đăng xuất khi đang mở app;
// (3) handleAuthError — backend trả 401 thì đá về login.
const PrivateRoute = ({ Component }) => {
  const location = useLocation();
  const token = getOwnerToken();
  // Token còn trong localStorage nhưng đã quá hạn (ví dụ đóng máy qua đêm rồi mở lại)
  // → dọn phiên chết ngay, KHÔNG để lọt vào dashboard rồi mới nhận 401.
  const expired = token ? isSessionExpired() : false;

  if (expired) {
    clearOwnerSession();
  }

  if (!token || expired) {
    if (location && location.pathname === PAGE_URL.LOGIN) {
      return null;
    }
    // `tokenExpired` để màn Login hiện toast "phiên đăng nhập đã hết hạn".
    return (
      <Navigate to={PAGE_URL.LOGIN} replace state={{ tokenExpired: expired }} />
    );
  }

  return <Component />;
};

// Màn chính: Owner Dashboard là route mặc định sau khi đăng nhập.
const OwnerDashboard = lazy(() => import('@app/pages/Owner/OwnerDashboard'));

// DiOwner — 6 báo cáo (scaffold). Lazy-load để tách bundle.
const ReportsIndex = lazy(() => import('@app/pages/Reports/ReportsIndex'));
const RevenueReport = lazy(
  () => import('@app/pages/Reports/RevenueReport/RevenueReport')
);
const SegmentReport = lazy(
  () => import('@app/pages/Reports/SegmentReport/SegmentReport')
);
const OccupancyReport = lazy(
  () => import('@app/pages/Reports/OccupancyReport/OccupancyReport')
);
const ForecastReport = lazy(
  () => import('@app/pages/Reports/ForecastReport/ForecastReport')
);
const AgencyReport = lazy(
  () => import('@app/pages/Reports/AgencyReport/AgencyReport')
);
const CompareReport = lazy(
  () => import('@app/pages/Reports/CompareReport/CompareReport')
);

// Pages and loading
const OwnerDashboardPage = withLoading(OwnerDashboard);
const ReportsIndexPage = withLoading(ReportsIndex);
const RevenueReportPage = withLoading(RevenueReport);
const SegmentReportPage = withLoading(SegmentReport);
const OccupancyReportPage = withLoading(OccupancyReport);
const ForecastReportPage = withLoading(ForecastReport);
const AgencyReportPage = withLoading(AgencyReport);
const CompareReportPage = withLoading(CompareReport);

export const AppRouter = () => {
  // Canh hạn token toàn app: hết hạn giữa chừng → tự đăng xuất về màn login,
  // kể cả khi người dùng không thao tác gì (không có request nào để nhận 401).
  useOwnerSession();

  return (
    // future flags: opt-in sớm hành vi React Router v7 — tắt cảnh báo deprecation
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <Routes>
        <Route path="/" element={<PrivateRoute Component={MainLayout} />}>
          {/* Owner Dashboard là màn mặc định (đã bỏ demo Dashboard). */}
          <Route
            index
            element={<Navigate to={PAGE_URL.OWNER_DASHBOARD} replace />}
          />
          <Route
            path={PAGE_URL.OWNER_DASHBOARD}
            element={<OwnerDashboardPage />}
          />
          <Route
            path={PAGE_URL.OWNER_REPORTS.INDEX}
            element={<ReportsIndexPage />}
          />
          <Route
            path={PAGE_URL.OWNER_REPORTS.REVENUE}
            element={<RevenueReportPage />}
          />
          <Route
            path={PAGE_URL.OWNER_REPORTS.SEGMENT}
            element={<SegmentReportPage />}
          />
          <Route
            path={PAGE_URL.OWNER_REPORTS.OCCUPANCY}
            element={<OccupancyReportPage />}
          />
          <Route
            path={PAGE_URL.OWNER_REPORTS.FORECAST}
            element={<ForecastReportPage />}
          />
          <Route
            path={PAGE_URL.OWNER_REPORTS.AGENCY}
            element={<AgencyReportPage />}
          />
          <Route
            path={PAGE_URL.OWNER_REPORTS.COMPARE}
            element={<CompareReportPage />}
          />
        </Route>
        <Route path={PAGE_URL.AUTH.INDEX} element={<Auth />}>
          <Route path={PAGE_URL.AUTH.LOGIN} element={<Login />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};
